import type { EntityFilterQuery } from '@backstage/catalog-client';
import { CATALOG_FILTER_EXISTS } from '@backstage/plugin-catalog-react';
import type { SearchResultType } from './search-tabs';
import { SEARCH_RESULT_TYPE } from './search-tabs';

/** Supported Q&A entity kinds in the Qeta plugin. */
export type QetaKind = 'question' | 'article' | 'collection';

/** A curated catalog or TechDocs entry surfaced in search results. */
export type FeaturedItem = {
  /** Entity metadata name (e.g. `backstage`, `machx`). */
  name: string;
  /** Catalog entity kind (e.g. `Component`, `System`, `Template`) or Qeta kind. */
  kind: string;
  /** Search tab where this item should appear. */
  type: SearchResultType;
  /** Display title override; used when `name` alone is not descriptive enough. */
  title?: string;
  /** Additional terms to match against when filtering featured items. */
  keywords?: string[];
};

/** Curated items promoted in search results when the user's query matches. */
export const FEATURED_ITEMS: FeaturedItem[] = [
  {
    name: 'architectural-decision-record-submission',
    kind: 'Template',
    type: SEARCH_RESULT_TYPE.SOFTWARE_CATALOG,
    keywords: ['adr', 'architecture', 'decision'],
  },
  {
    name: 'architectural-decision-record-submission',
    kind: 'Template',
    type: SEARCH_RESULT_TYPE.TECHDOCS,
    keywords: ['adr', 'architecture', 'decision'],
  },
  {
    name: 'backstage',
    kind: 'Component',
    type: SEARCH_RESULT_TYPE.TECHDOCS,
    keywords: ['portal', 'developer', 'internal'],
  },
  {
    name: 'erb',
    kind: 'Component',
    type: SEARCH_RESULT_TYPE.TECHDOCS,
    keywords: ['erb', 'engineering', 'review', 'board'],
  },
  {
    name: 'hapi',
    kind: 'Component',
    type: SEARCH_RESULT_TYPE.TECHDOCS,
    keywords: ['api', 'integration'],
  },
  {
    name: 'machx',
    kind: 'System',
    type: SEARCH_RESULT_TYPE.TECHDOCS,
    keywords: ['platform', 'runtime', 'container'],
  },
  {
    name: 'mcs-accelerator',
    kind: 'System',
    type: SEARCH_RESULT_TYPE.TECHDOCS,
    keywords: ['mcs', 'accelerator'],
  },
  {
    name: 'threads',
    kind: 'Component',
    type: SEARCH_RESULT_TYPE.TECHDOCS,
    keywords: ['messaging', 'communication'],
  },
  {
    name: '1',
    kind: 'collection',
    type: SEARCH_RESULT_TYPE.QETA,
    title: 'Announcements by the Backstage Team',
    keywords: ['collection', 'qeta', 'backoverflow', 'notification'],
  },
];

/** Build a catalog filter that matches a TechDocs-enabled entity by name and kind. */
export const buildTechDocsFilter = (
  name: string,
  kind: string,
): EntityFilterQuery => ({
  'metadata.annotations.backstage.io/techdocs-ref': CATALOG_FILTER_EXISTS,
  'metadata.name': name,
  kind,
});

/** Build a catalog filter that matches an entity by name and kind. */
export const buildCatalogFilter = (
  name: string,
  kind: string,
): EntityFilterQuery => ({
  'metadata.name': name,
  kind,
});

/**
 * Return featured items whose name, title, or keywords match any word in
 * `term`, filtered to the given search result `types`. Duplicates (same
 * name + kind) are removed, keeping the first occurrence.
 */
export const getMatchingItems = (term: string, types: string[]) => {
  /** Individual lowercase tokens extracted from the search term. */
  const words = term
    .toLowerCase()
    .split(/\s+/)
    .filter(w => w.length > 0);
  /** Returns true if any entry in `searchable` contains the given `word`. */
  const matchesWord = (searchable: string[], word: string) =>
    searchable.some(s => s.includes(word));
  /** Featured items that match both the active type filter and at least one search word. */
  const matched = FEATURED_ITEMS.filter(item => {
    /** True when no type filter is active or the item's type is selected. */
    const typeMatch = types.length === 0 || types.includes(item.type);
    /** Lowercased searchable strings built from name, title, and keywords. */
    const searchable = [
      item.name,
      ...(item.title ? [item.title] : []),
      ...(item.keywords ?? []),
    ].map(s => s.toLowerCase());
    /** True when at least one search word appears in the searchable fields. */
    const termMatch = words.some(word => matchesWord(searchable, word));
    return typeMatch && termMatch;
  });
  /** Deduplicate by name + kind, keeping the first occurrence. */
  return matched.filter(
    (item, index) =>
      matched.findIndex(
        other => other.name === item.name && other.kind === item.kind,
      ) === index,
  );
};
